import type { ModelInfo } from '@paradigm/terminal-core';

import { formatVersionLabel, parseModelId } from './provider-groups';

/**
 * Friendly label for a model id in the `provider/company/model/version`
 * format. Derives the display name directly from the structured id segments
 * — no static lookup table needed.
 *
 * Examples:
 *   `bedrock/anthropic/opus/4.8`    → `Opus 4.8`
 *   `bedrock/deepseek/deepseek-v3/` → `DeepSeek V3`
 *   `qwen/qwen/qwen-3.7/max`       → `Qwen 3.7 max`
 */
export function defaultModelLabel(id: string): string {
  const parsed = parseModelId(id);
  // If parsing yields a model segment, derive the label from it.
  if (parsed.model.length > 0) {
    return formatVersionLabel(parsed);
  }
  // Unparseable → return the raw id.
  return id;
}

/**
 * Normalize a model id for comparison: strip the provider prefix (first
 * segment) so `bedrock/anthropic/opus/4.8` and `fireworks-ai/anthropic/opus/4.8`
 * compare equal. Also handles the legacy `[region.]provider.model` format
 * and the `amazon-bedrock/` prefix for backward compatibility.
 */
export function normalizeModelId(id: string): string {
  if (id.includes('/')) {
    // Strip dtmodel/ prefix, then the LiteLLM provider (first remaining
    // segment: bedrock, fireworks-ai, etc.) so the same model from different
    // providers compares equal: company/model/version.
    let rest = id.replace(/^dtmodel\//u, '');
    const slash = rest.indexOf('/');
    if (slash > 0) rest = rest.slice(slash + 1);
    return rest.replace(/\/(?:DEFAULT|default)$/u, '').replace(/\/$/u, '');
  }
  // Legacy format: strip amazon-bedrock/ prefix and two-letter region.
  return id.replace(/^amazon-bedrock\//u, '').replace(/^[a-z]{2}\./u, '');
}

/**
 * The model to pre-select when the user hasn't picked one — Opus, from either
 * source. Prefers a non-legacy Opus explicitly, then falls back to the first
 * non-legacy model, then anything. `null` only when the list is empty.
 */
export function pickDefaultModelId(
  models: readonly ModelInfo[],
): string | null {
  const opus = models.find(
    (m) => m.legacy !== true && normalizeModelId(m.id).includes('opus'),
  );
  if (opus != null) return opus.id;
  const nonLegacy = models.find((m) => m.legacy !== true);
  return (nonLegacy ?? models[0])?.id ?? null;
}
